import * as types from 'store/mutation-types'
import cookie from './cookie'
import api from './api'
import authConst from './authConst'
import router from 'app/routes'
import store from 'store'
import Vue from 'vue'

const registerAuthData = async (dispatch, casUserData) => {
  if (!casUserData) {
    casUserData = await api.getUserData()
  }
  const collaborationCentralBWToken = cookie.get(authConst.tokenCentralBWName)
  let currentUserData
  let currentToken = collaborationCentralBWToken || casUserData?.access_token

  if (currentToken) {
    api.setProfileApiAuthenticationHeader(currentToken)
    api.setProductApiAuthenticationHeader(currentToken)
    api.setBusinessWorkspaceApiAuthenticationHeader(currentToken)
    await dispatch(types.SET_USER_PHOTO_URL)
  }

  if (casUserData && !collaborationCentralBWToken) {
    currentToken = casUserData.expired ? null : casUserData.id_token
    currentUserData = casUserData
  } else if (collaborationCentralBWToken) {
    console.debug('Fallback to collaboration central authentication')
    try {
      currentUserData = await api.getLegacyUserData()
    } catch (error) {
      console.error(error)
      await dispatch(types.LOGOUT_USER)
      await dispatch('notification/SHOW_TRANSLATED_NOTIFICATION', ['errorMessages.internalServerError', 'danger'], { root: true })
      return null
    }
  }

  if (currentToken && currentUserData) {
    currentUserData.userTypeInfo = await api.getUserTypeInfo()
    dispatch(types.SET_LOGIN, currentToken)
    dispatch(types.SET_USER, currentUserData)
  } else {
    if (store.state.auth.token) {
      await dispatch(types.REMOVE_LOGIN)
      await dispatch(types.REMOVE_CURRENT_PRODUCT)
    }
    router.push({name: 'login'})
    return new Error('Unauthorized!')
  }

  return currentToken
}

export default {
  [types.LOGIN_USER] () {
    return api.redirectToLogin()
  },
  [types.PROCESS_LOGIN_RESPONSE] ({ dispatch }) {
    return api.processLoginResponse().then(() => {
      Vue.hotLauncher('send', 'login')
      // handle silent renew user load
      api.onUserLoaded((user) => {
        registerAuthData(dispatch, user)
      })
      return registerAuthData(dispatch)
    })
  },
  async [types.CHECK_LOGIN_ACCESS] ({ state, dispatch }) {
    let currentToken = false

    if (!state.token || !state.user) {
      currentToken = await registerAuthData(dispatch)
    }
    if (currentToken) {
      Vue.hotLauncher('send', 'login')
    }
  },
  [types.SET_LOGIN] ({ commit }, token) {
    commit(types.SET_LOGIN, token)
  },
  [types.REMOVE_LOGIN] ({ commit }) {
    cookie.delete(authConst.tokenCentralBWName)
    commit(types.REMOVE_LOGIN)
  },
  [types.SET_USER] ({ commit }, { profile: { name, id, authorities, locale, email }, userTypeInfo }) {
    Vue.hotLauncher('userName', name)
    Vue.hotLauncher('userId', id)

    commit(types.SET_USER, {
      id,
      name,
      authorities,
      locale,
      email,
      userTypeInfo
    })
  },
  async [types.SET_CURRENT_PRODUCT] ({ state, commit, dispatch }, { productId }) {
    const checkCurrentProduct = await dispatch(types.CHECK_PRODUCT_OWNER, productId)
    if (!checkCurrentProduct) {
      await dispatch(types.PRODUCT_ACCESS_DENIED, { redirect: false })
      return false
    }
    await dispatch('dashboard/CLEAR_CHECKOUT_DATA', null, { root: true })
    commit(types.SET_CURRENT_PRODUCT, productId)
    await dispatch(types.SET_CURRENT_PRODUCT_DATA, productId)
    return true
  },
  async [types.SET_USER_PHOTO_URL] ({commit}) {
    const userPhotoUrl = await api.getUserProfileImageUrl()
    commit(types.SET_USER_PHOTO_URL, userPhotoUrl)
  },
  async [types.SET_CURRENT_PRODUCT_DATA] ({ commit }, productId) {
    const productData = await api.getProductData(productId)

    commit(types.SET_CURRENT_PRODUCT_DATA, productData.data)
  },
  [types.REMOVE_CURRENT_PRODUCT] ({ commit }) {
    commit(types.REMOVE_CURRENT_PRODUCT)
  },
  async [types.LOGOUT_USER] ({ dispatch }, { triggeredByUser } = {}) {
    await dispatch(types.REMOVE_CURRENT_PRODUCT)
    await dispatch(types.REMOVE_LOGIN)
    if (triggeredByUser) {
      Vue.analytics('send', {
        hitType: 'event',
        eventCategory: 'Redirects',
        eventAction: 'logout'
      })
      await dispatch('notification/SHOW_TRANSLATED_NOTIFICATION', ['login.logoutSuccess', 'success'], { root: true })
    }
    api.redirectToLogout()
  },
  [types.GET_PRODUCTS] () {
    return api.getProducts()
  },
  async [types.CHECK_PRODUCT_OWNER] ({ dispatch }, productId) {
    const productsList = await dispatch(types.GET_PRODUCTS)

    return productsList.some(current => {
      return current.id === parseInt(productId)
    })
  },
  async [types.PRODUCT_ACCESS_DENIED] ({ dispatch }, { redirect = true } = {}) {
    await dispatch(types.REMOVE_CURRENT_PRODUCT)
    await dispatch('notification/SHOW_TRANSLATED_NOTIFICATION', ['errorMessages.productAccessDenied', 'info'], { root: true })
    if (redirect) {
      return router.push('/')
    }
  }
}
